/**
 * <site-nav> — the primary navigation.
 *
 * ◆ Routes for phases that have not landed are shown but disabled, with the
 * phase named. A nav that silently omits half the site makes it look finished
 * when it is not; one that links to an empty page is worse.
 *
 * © Manish Jagdish Thatte
 */

import { FEATURES } from '../config.js';
import { CHART_TOOL_ROUTE_SET, getActiveBirth } from '../utils/active-birth.js';
import { onLocaleChange, t } from '../utils/i18n.js';

/**
 * ◆ Two tiers. The first row is what most visits are for; everything else sits
 * under "More" so that a dozen links do not crowd a phone. `ready` still drives a
 * pending (struck-through) entry for a route whose feature flag is off.
 */
const ITEMS = [
  { route: '', key: 'nav.home', ready: true },
  { route: 'kundali', key: 'nav.kundali', ready: true },
  { route: 'panchang', key: 'nav.panchang', ready: FEATURES.panchanga, phase: 3 },
  { route: 'dasha', key: 'nav.dasha', ready: FEATURES.dashas, phase: 7 },
  { route: 'transit', key: 'nav.transit', ready: FEATURES.transit, phase: 9 },
  { route: 'matching', key: 'nav.matching', ready: FEATURES.matching, phase: 12 },
  { route: 'palm', key: 'nav.palm', ready: FEATURES.hasta, phase: 15 },
  { route: 'learn', key: 'nav.learn', ready: FEATURES.learn, phase: 17 },
  { route: 'offerings', key: 'nav.offerings', ready: true },
  // — under "More" —
  { route: 'muhurta', key: 'nav.muhurta', ready: FEATURES.panchanga, phase: 3, more: true },
  { route: 'varga', key: 'nav.varga', ready: FEATURES.vargas, phase: 4, more: true },
  { route: 'strength', key: 'nav.strength', ready: FEATURES.strength, phase: 6, more: true },
  { route: 'refinements', key: 'nav.refinements', ready: FEATURES.refinements, phase: 5, more: true },
  { route: 'yogas', key: 'nav.yogas', ready: FEATURES.yogas, phase: 8, more: true },
  { route: 'tajaka', key: 'nav.tajaka', ready: FEATURES.tajaka, phase: 10, more: true },
  { route: 'longevity', key: 'nav.longevity', ready: FEATURES.longevity, phase: 11, more: true },
  { route: 'remedies', key: 'nav.remedies', ready: FEATURES.remedies, phase: 11, more: true },
  { route: 'rectify', key: 'nav.rectify', ready: FEATURES.rectify, phase: 13, more: true },
  { route: 'mundane', key: 'nav.mundane', ready: FEATURES.mundane, phase: 14, more: true },
  { route: 'reading', key: 'nav.reading', ready: FEATURES.reading, phase: 16, more: true },
  { route: 'correlation', key: 'nav.correlation', ready: FEATURES.correlation, phase: 16, more: true },
  { route: 'sources', key: 'nav.sources', ready: FEATURES.sources, phase: 17, more: true },
  { route: 'saved', key: 'nav.saved', ready: true, more: true },
  { route: 'settings', key: 'nav.settings', ready: true, more: true },
  { route: 'account', key: 'nav.account', ready: true, more: true },
  { route: 'signin', key: 'nav.signin', ready: true, more: true },
  { route: 'signup', key: 'nav.signup', ready: true, more: true },
  { route: 'console', key: 'nav.console', ready: FEATURES.readings, phase: 20, more: true },
  { route: 'about', key: 'nav.about', ready: true, more: true }
];

function entry(item) {
  const li = document.createElement('li');
  if (item.ready) {
    const a = document.createElement('a');
    const active = getActiveBirth();
    a.href = (active?.id && CHART_TOOL_ROUTE_SET.has(item.route))
      ? `#/${item.route}/${encodeURIComponent(active.id)}`
      : `#/${item.route}`;
    a.textContent = t(item.key);
    a.dataset.route = item.route;
    li.append(a);
  } else {
    const span = document.createElement('span');
    span.className = 'nav__pending';
    span.textContent = t(item.key);
    span.title = `Not built yet — phase ${item.phase}`;
    span.setAttribute('aria-disabled', 'true');
    li.append(span);
  }
  return li;
}

export class SiteNav extends HTMLElement {
  connectedCallback() {
    if (this._built) return;
    this._built = true;
    this.render();
    this._onHash = () => this.markCurrent();
    window.addEventListener('hashchange', this._onHash);
    // ⚠ The bundle load, not the setting change — see i18n.js § localeListeners.
    // This component is upgraded before boot() has awaited loadLocale().
    this._unsubscribe = onLocaleChange(() => this.render());
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this._onHash);
    this._unsubscribe?.();
  }

  render() {
    this.textContent = '';
    const nav = document.createElement('nav');
    nav.className = 'nav';
    nav.setAttribute('aria-label', 'Primary');
    const ul = document.createElement('ul');

    for (const item of ITEMS.filter((i) => !i.more)) ul.append(entry(item));

    // The secondary routes: a <details>, so it works without script beyond this
    // component and is operable from the keyboard with no extra code.
    const more = document.createElement('li');
    const details = document.createElement('details');
    details.className = 'nav__more';
    const summary = document.createElement('summary');
    summary.textContent = t('nav.more');
    const sub = document.createElement('ul');
    sub.className = 'nav__more-list';
    for (const item of ITEMS.filter((i) => i.more)) sub.append(entry(item));
    details.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && details.open) { details.open = false; summary.focus(); }
    });
    details.append(summary, sub);
    more.append(details);
    ul.append(more);

    nav.append(ul);
    this.append(nav);
    this.markCurrent();
  }

  markCurrent() {
    const route = (location.hash.replace(/^#\/?/, '').split('/')[0]) || '';
    const active = getActiveBirth();
    for (const a of this.querySelectorAll('a[data-route]')) {
      // Keep tool links pointed at the active chart while it is pinned.
      if (active?.id && CHART_TOOL_ROUTE_SET.has(a.dataset.route)) {
        a.href = `#/${a.dataset.route}/${encodeURIComponent(active.id)}`;
      } else if (CHART_TOOL_ROUTE_SET.has(a.dataset.route)) {
        a.href = `#/${a.dataset.route}`;
      }
      const isCurrent = a.dataset.route === route;
      a.toggleAttribute('data-current', isCurrent);
      if (isCurrent) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    }
    const details = this.querySelector('.nav__more');
    if (details) {
      details.open = false;
      details.toggleAttribute('data-current', !!details.querySelector('a[data-current]'));
    }
  }
}
customElements.define('site-nav', SiteNav);
